import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { Nav, Footer } from "@/components/Chrome";
import { NeuralField } from "@/components/NeuralField";
import { useAuth } from "@/contexts/AuthContext";
import { startCheckout, billingStatus, openBillingPortal } from "@/lib/api";

function firstChargeDate() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

const FEATURES = [
  "Unlimited prompt audits & readiness scores",
  "Full Intent Map + Requirement & Intent Retention",
  "PromptDiff and auto-rewritten prompts",
  "Personalized Build Intelligence & history trends",
  "Priority processing",
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    if (user) billingStatus().then(setSub).catch(() => {});
  }, [user]);

  const chargeDate = firstChargeDate();

  const onSubscribe = async () => {
    if (!user) {
      toast.message("Create a free account first to start your trial.");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const { checkout_url } = await startCheckout();
      window.location.href = checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) toast.success("You're already on CoreText Pro.");
      else if (err?.response?.status === 503) toast.error("Billing isn't switched on yet. Check back soon.");
      else toast.error(detail || "Couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  const onManage = async () => {
    setLoading(true);
    try {
      const { portal_url } = await openBillingPortal();
      window.location.href = portal_url;
    } catch {
      toast.error("Couldn't open the billing portal.");
      setLoading(false);
    }
  };

  const hasAccess = sub?.has_access;
  const isAdmin = !!user?.is_admin;

  return (
    <div className="relative min-h-screen text-[var(--text)]">
      <NeuralField />
      <Nav />
      <main className="relative max-w-3xl mx-auto px-5 pt-14 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="font-mono text-[10px] tracking-[0.3em] text-neon-amber mb-2">CORETEXT PRO</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white leading-none">
            {isAdmin ? "You have full access." : "Start 30 days free."}
          </h1>
          <p className="font-mono text-sm text-[var(--muted)] mt-4 max-w-lg leading-relaxed">
            {isAdmin
              ? "As a CoreText Owner, every Pro capability is already unlocked — no trial or payment needed."
              : "Everything CoreText does, unlimited — try it for a month before you pay a cent."}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.1 }}
          className="glass neon-border rounded-2xl p-8 mt-10 scanlines"
          data-testid="pro-plan-card"
        >
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <p className="font-display text-2xl font-bold text-white flex items-center gap-2">
                <Sparkles size={20} className="text-neon-cyan" /> CoreText Pro
              </p>
              <p className="font-mono text-sm text-[var(--muted)] mt-1">Billed monthly · cancel anytime</p>
            </div>
            <div className="text-right">
              {isAdmin ? (
                <span className="font-display text-3xl font-bold text-neon-cyan text-glow-cyan" data-testid="pro-price">Unlimited</span>
              ) : (
                <>
                  <span className="font-display text-4xl font-bold text-neon-cyan text-glow-cyan" data-testid="pro-price">$3.99</span>
                  <span className="font-mono text-sm text-[var(--muted)]">/month</span>
                </>
              )}
            </div>
          </div>

          <ul className="mt-7 space-y-3">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 font-mono text-sm text-[var(--text)]">
                <Check size={16} className="text-neon-cyan mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>

          {isAdmin ? (
            <div className="mt-8">
              <div
                data-testid="owner-unlimited-banner"
                className="glass rounded-xl p-4 font-mono text-sm text-neon-cyan border border-neon-cyan/40 flex items-center gap-2"
              >
                <ShieldCheck size={16} className="shrink-0" /> CoreText Owner · Unlimited
              </div>
              <p className="font-mono text-[11px] text-[var(--muted)] mt-3 leading-relaxed">
                Owner and admin accounts have unlimited CoreText access. No trial, no subscription, no payment.
              </p>
            </div>
          ) : hasAccess ? (
            <div className="mt-8">
              <div className="glass rounded-xl p-4 font-mono text-sm text-neon-cyan border border-neon-cyan/40 mb-4" data-testid="pro-active-banner">
                You're on CoreText Pro ({sub.status}
                {sub.cancel_at_period_end ? " · cancels at period end" : ""}).
              </div>
              <button
                data-testid="manage-subscription-button"
                onClick={onManage}
                disabled={loading}
                className="w-full rounded-lg font-display font-bold tracking-wide py-3.5 flex items-center justify-center gap-2 border border-neon-cyan/40 text-white hover:glow-cyan transition-shadow disabled:opacity-50"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : "Manage subscription"}
              </button>
            </div>
          ) : (
            <div className="mt-8">
              <button
                data-testid="start-trial-button"
                onClick={onSubscribe}
                disabled={loading}
                style={{ backgroundColor: "#22d3ee", color: "#05060a" }}
                className="w-full rounded-lg font-display font-bold tracking-wide py-3.5 flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:glow-cyan transition-transform transition-shadow duration-200 disabled:opacity-50"
              >
                {loading ? <><Loader2 size={18} className="animate-spin" /> STARTING…</> : "Start 30-day free trial"}
              </button>
              <p
                data-testid="billing-disclosure"
                className="font-mono text-[11px] text-[var(--muted)] mt-4 leading-relaxed flex gap-2"
              >
                <ShieldCheck size={14} className="text-neon-cyan shrink-0 mt-0.5" />
                <span>
                  30 days free · <span className="text-white">$0 due today</span>. Then{" "}
                  <span className="text-white">$3.99/month starting on {chargeDate}</span>.
                  Cancel anytime before then to avoid being charged. Subscription renews monthly until canceled.
                </span>
              </p>
            </div>
          )}
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
